import React, { useState } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";

import ConfirmationDialog from "./ConfirmationDialog";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "#1C1E20",
      maxWidth: "1200px",
      width: "90%",
    },
  },
  dialogTitle: {
    backgroundColor: "rgba(232, 163, 61, 0.1)",
    borderBottom: "1px solid rgba(232, 163, 61, 0.3)",
  },
  title: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1.5rem",
  },
  content: {
    padding: "2rem",
  },
  section: {
    marginBottom: "2rem",
  },
  sectionTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#E8A33D",
    marginBottom: "1rem",
  },
  comparisonGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "2rem",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  playerCard: {
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "8px",
    padding: "1.5rem",
    border: "1px solid rgba(232, 163, 61, 0.2)",
  },
  playerCardTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#E8A33D",
    marginBottom: "1rem",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0.5rem 0",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
  },
  infoLabel: {
    color: "rgba(255,255,255,0.7)",
    fontSize: "0.9rem",
  },
  infoValue: {
    color: "rgba(255,255,255,0.9)",
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  actionButtons: {
    display: "flex",
    gap: "1rem",
    flexWrap: "wrap",
    padding: "1.5rem",
    backgroundColor: "rgba(232, 163, 61, 0.05)",
  },
  validateButton: {
    backgroundColor: "#00C853",
    "&:hover": {
      backgroundColor: "#00E676",
    },
  },
  dismissButton: {
    backgroundColor: "#FFC107",
    "&:hover": {
      backgroundColor: "#FFD54F",
    },
  },
  falseReportButton: {
    backgroundColor: "#F44336",
    "&:hover": {
      backgroundColor: "#FF5252",
    },
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: "2rem",
  },
});

const tierConfig = {
  ELITE: { label: "ELITE", color: "#00C853" },
  HIGH: { label: "HIGH", color: "#64DD17" },
  GOOD: { label: "GOOD", color: "#FFC107" },
  FAIR: { label: "FAIR", color: "#FF9800" },
  LOW: { label: "LOW", color: "#F44336" },
};

function ReportDetailModal({ classes, open, report, userId, onClose, onComplete }) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleDecisionClick = (decision) => {
    setSelectedDecision(decision);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDecision = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/reputation/review-report/adpass/${userId}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reportId: report._id,
            decision: selectedDecision,
            adminNotes: "",
          }),
        }
      );

      const result = await response.json();
      if (result.ok) {
        setConfirmDialogOpen(false);
        onComplete();
      } else {
        alert("Error processing decision: " + (result.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Error submitting decision:", err);
      alert("Error submitting decision");
    } finally {
      setSubmitting(false);
    }
  };

  const reporterTier = report.reporterReputation?.trustTier || "GOOD";
  const reportedTier = report.reportedPlayerReputation?.trustTier || "GOOD";
  const reporterTierConfig = tierConfig[reporterTier] || tierConfig.GOOD;
  const reportedTierConfig = tierConfig[reportedTier] || tierConfig.GOOD;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className={classes.dialog}>
        <DialogTitle className={classes.dialogTitle}>
          <Typography className={classes.title}>Report Review</Typography>
          <Typography variant="body2" style={{ color: "rgba(255,255,255,0.7)", marginTop: "0.5rem" }}>
            Report ID: {report._id}
          </Typography>
        </DialogTitle>

        <DialogContent className={classes.content}>
          {/* Report Details */}
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>Report Details</Typography>
            <Box className={classes.playerCard}>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Offense Type:</span>
                <span className={classes.infoValue}>{report.offenseType || "N/A"}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Description:</span>
                <span className={classes.infoValue}>{report.description || "No description"}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Match ID:</span>
                <span className={classes.infoValue}>{report.matchId || "N/A"}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Date:</span>
                <span className={classes.infoValue}>
                  {new Date(report.createdAt).toLocaleString()}
                </span>
              </div>
            </Box>
          </div>

          {/* Player Comparison */}
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>Player Profiles</Typography>
            <div className={classes.comparisonGrid}>
              {/* Reporter Card */}
              <div className={classes.playerCard}>
                <Typography className={classes.playerCardTitle}>REPORTER</Typography>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>SteamID:</span>
                  <span className={classes.infoValue}>{report.reporterSteamId || "N/A"}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Trust Tier:</span>
                  <Chip
                    label={reporterTierConfig.label}
                    style={{
                      backgroundColor: reporterTierConfig.color,
                      color: "#fff",
                      fontWeight: 700,
                    }}
                    size="small"
                  />
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Overall Score:</span>
                  <span className={classes.infoValue}>
                    {report.reporterReputation?.overallTrustScore || 50} / 100
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Reporter Quality:</span>
                  <span className={classes.infoValue}>
                    {report.reporterReputation?.dimensions?.reporterQuality?.score || 50} / 100
                  </span>
                </div>
                <Divider style={{ margin: "1rem 0", backgroundColor: "rgba(255,255,255,0.1)" }} />
                <Typography variant="body2" style={{ color: "#E8A33D", fontWeight: 700, marginTop: "1rem" }}>
                  Match Performance
                </Typography>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>K/D:</span>
                  <span className={classes.infoValue}>
                    {report.reporterMatchPerformance?.kills || 0} /{" "}
                    {report.reporterMatchPerformance?.deaths || 0}
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>ADR:</span>
                  <span className={classes.infoValue}>{report.reporterMatchPerformance?.adr || 0}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Result:</span>
                  <span className={classes.infoValue}>{report.reporterMatchPerformance?.outcome || "N/A"}</span>
                </div>
              </div>

              {/* Reported Player Card */}
              <div className={classes.playerCard}>
                <Typography className={classes.playerCardTitle}>REPORTED PLAYER</Typography>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>SteamID:</span>
                  <span className={classes.infoValue}>{report.reportedSteamId || "N/A"}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Trust Tier:</span>
                  <Chip
                    label={reportedTierConfig.label}
                    style={{
                      backgroundColor: reportedTierConfig.color,
                      color: "#fff",
                      fontWeight: 700,
                    }}
                    size="small"
                  />
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Overall Score:</span>
                  <span className={classes.infoValue}>
                    {report.reportedPlayerReputation?.overallTrustScore || 50} / 100
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Integrity Score:</span>
                  <span className={classes.infoValue}>
                    {report.reportedPlayerReputation?.dimensions?.integrity?.score || 50} / 100
                  </span>
                </div>
                <Divider style={{ margin: "1rem 0", backgroundColor: "rgba(255,255,255,0.1)" }} />
                <Typography variant="body2" style={{ color: "#E8A33D", fontWeight: 700, marginTop: "1rem" }}>
                  Match Performance
                </Typography>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>K/D:</span>
                  <span className={classes.infoValue}>
                    {report.reportedPlayerMatchPerformance?.kills || 0} /{" "}
                    {report.reportedPlayerMatchPerformance?.deaths || 0}
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>ADR:</span>
                  <span className={classes.infoValue}>{report.reportedPlayerMatchPerformance?.adr || 0}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Result:</span>
                  <span className={classes.infoValue}>{report.reportedPlayerMatchPerformance?.outcome || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>

        <DialogActions className={classes.actionButtons}>
          <Button
            variant="contained"
            className={classes.validateButton}
            onClick={() => handleDecisionClick("VALIDATED")}
            disabled={submitting}
          >
            Validate Report
          </Button>
          <Button
            variant="contained"
            className={classes.dismissButton}
            onClick={() => handleDecisionClick("DISMISSED")}
            disabled={submitting}
          >
            Dismiss Report
          </Button>
          <Button
            variant="contained"
            className={classes.falseReportButton}
            onClick={() => handleDecisionClick("FALSE_REPORT")}
            disabled={submitting}
          >
            Mark as False Report
          </Button>
          <Button variant="outlined" onClick={onClose} disabled={submitting} style={{ marginLeft: "auto" }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        decision={selectedDecision}
        report={report}
        onConfirm={handleConfirmDecision}
        onCancel={() => setConfirmDialogOpen(false)}
        submitting={submitting}
      />
    </>
  );
}

ReportDetailModal.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  report: PropTypes.object.isRequired,
  userId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default withStyles(styles)(ReportDetailModal);
